import { useCallback, useEffect, useState } from "react";

/**
 * Delay mounting something expensive until it is near the viewport, then leave
 * it mounted.
 *
 * Every host component that frames an animation embed needs this, and until now
 * each one hand-rolled it — four near-identical `IntersectionObserver` blocks,
 * and one component (`AgentAccessFabricAnimation`) that simply never got one, so
 * five animation bundles loaded on page load whether or not the visitor ever
 * scrolled to them.
 *
 * `loading="lazy"` on the iframe is NOT a substitute. It is a hint about the
 * *request*, and Chrome's viewport threshold for it is generous enough that an
 * embed 8000px below the fold still loads and starts animating immediately —
 * `HomeHero2026.tsx` already carries a comment saying exactly this, and the CTA
 * embed proved it: measured live at `scrollY: 0`, `readyState: "complete"`, with
 * a rendering canvas. See docs/rfc/0002-homepage-cpu-performance.md.
 *
 * Scope note: this gates the MOUNT only. Whether an embed keeps animating after
 * it scrolls back out of view is the embed's own business, handled by
 * `public/embed-runtime/c1-embed.js`. Unmounting on exit is deliberately not
 * offered here: it would replay each embed's poster-first settle-and-cross-fade
 * on re-entry, which is a visible behaviour change, and pausing the loop already
 * recovers the CPU.
 *
 * ## Why the margin is this generous
 *
 * Loading and animating are separate concerns and want opposite margins.
 * Loading should start EARLY — measured mount-to-first-paint on localhost with a
 * warm cache: 63ms for the lightest embed, 251ms for `c1-marketecture`, and
 * **584ms** for `cta-cursor-embed` (600KB of Three.js to parse, a 16,902-vertex
 * extrude to build, shaders to compile). A real network with a cold cache is
 * worse. At a normal scroll speed of roughly 800–1500 px/s, the 200px margin
 * these components started with buys only 130–250ms of lead, so the graphic was
 * still visibly popping in on arrival.
 *
 * Animating should start LATE, and does: the runtime gates the render loop
 * separately at 100px, and primes exactly one frame at registration so a
 * preloaded embed is fully drawn while still costing nothing per frame. That
 * combination is what makes a generous mount margin safe — it buys load time
 * without buying CPU. Before the runtime existed, widening this would have meant
 * animating a screen and a half of off-screen embeds.
 *
 * 1200px is ~1–1.5 viewports of lead. It is deliberately not larger: the
 * homepage carries nine embeds, and a margin approaching the page height would
 * mount most of them at once and re-create the "everything loads up front"
 * problem this hook exists to fix.
 *
 * ## Why the ref is state-backed and not a `useRef`
 *
 * The box being observed may not exist on the first render, so attachment has
 * to be something this hook can react to.
 *
 * `AgentAccessFabricAnimation` resolves its `desktop_only` breakpoint in an
 * effect and returns `null` until it knows which side to show, so on the first
 * pass there is no element for a ref to point at. With a plain `useRef` the
 * effect below ran once, found `ref.current === null`, created no observer, and
 * then never ran again — neither of its dependencies changed when the element
 * finally attached. `revealed` stayed `false` for the lifetime of the page and
 * the graphic never mounted at all, leaving a correctly-sized empty band where
 * the animation should be. Storing the node in state makes attachment a render,
 * which re-runs the effect and observes the element that now exists.
 *
 * Any caller that mounts its box conditionally depends on this, so it belongs
 * here rather than in a hook-ordering rule each caller has to remember.
 *
 * @param rootMargin how early to mount, relative to the viewport.
 * @returns a callback ref for the placeholder box, whether to mount yet, and
 *   the observed node (use this instead of `.current` — the first element is a
 *   callback, not a ref object).
 */
export function useRevealOnApproach<T extends HTMLElement = HTMLDivElement>(
  rootMargin = "1200px 0px",
): [(el: T | null) => void, boolean, T | null] {
  const [node, setNode] = useState<T | null>(null);
  const [revealed, setRevealed] = useState(false);
  const setRef = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (revealed || !node) return;
    // No IntersectionObserver (very old browsers, some test environments):
    // mount immediately rather than never showing the graphic at all.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [revealed, rootMargin, node]);

  return [setRef, revealed, node];
}
